import { useCallback } from 'react';
import { applicationsClient } from '@terra/shared-rest-clients-applications';
import type { FetchMfaWebhookConfigResult } from '@terra/ui-design-system';
import { getCurrentGatewayUrl } from '../../../utils/environmentDetection';
import { getAuthHeaders } from '../../../utils/authHeaders';

const NO_TARGET_ERROR = 'No target or company selected';

const orchestratorClientOptions = () => ({
  baseUrl: `${getCurrentGatewayUrl()}/orchestrator`,
});

export function useTargetCredentialActions({
  applicationId,
  tenantId,
}: {
  applicationId?: string;
  tenantId?: string;
}) {
  const handleFetchMfaMessages = useCallback(
    async ({
      credentialId,
      mfaConfigId,
    }: {
      credentialId: string;
      mfaConfigId: string;
    }) => {
      if (!applicationId || !tenantId) {
        return { success: false, error: NO_TARGET_ERROR };
      }

      try {
        const authHeaders = await getAuthHeaders();
        const response = await applicationsClient.getMfaMessages({
          params: { id: applicationId, credentialId, mfaConfigId },
          overrideClientOptions: orchestratorClientOptions(),
          extraHeaders: { ...authHeaders },
        });

        if (response.status === 200) {
          return {
            success: response.body.success,
            messages: response.body.messages,
            otpCode: response.body.otpCode,
            error: response.body.error,
          };
        }

        return { success: false, error: 'Failed to fetch MFA messages' };
      } catch (error) {
        console.error('Failed to fetch MFA messages:', error);

        return { success: false, error: 'Failed to fetch MFA messages' };
      }
    },
    [applicationId, tenantId],
  );

  const handleFetchCredentialInbox = useCallback(
    async ({ credentialId }: { credentialId: string }) => {
      if (!applicationId || !tenantId) {
        return { success: false, error: NO_TARGET_ERROR };
      }

      try {
        const authHeaders = await getAuthHeaders();
        const response = await applicationsClient.getCredentialInboxMessages({
          params: { id: applicationId, credentialId },
          overrideClientOptions: orchestratorClientOptions(),
          extraHeaders: { ...authHeaders },
        });

        if (response.status === 200) {
          return {
            success: response.body.success,
            messages: response.body.messages,
            emailAddress: response.body.emailAddress,
            error: response.body.error,
          };
        }

        return {
          success: false,
          error: 'Failed to fetch credential inbox messages',
        };
      } catch (error) {
        console.error('Failed to fetch credential inbox messages:', error);

        return {
          success: false,
          error: 'Failed to fetch credential inbox messages',
        };
      }
    },
    [applicationId, tenantId],
  );

  const handleFetchMfaWebhookConfig = useCallback(
    async ({
      credentialId,
      mfaConfigId,
    }: {
      credentialId: string;
      mfaConfigId: string;
    }): Promise<FetchMfaWebhookConfigResult> => {
      if (!applicationId || !tenantId) {
        return { success: false, error: NO_TARGET_ERROR };
      }

      try {
        const authHeaders = await getAuthHeaders();
        const response = await applicationsClient.getMfaWebhookConfig({
          params: { id: applicationId, credentialId, mfaConfigId },
          overrideClientOptions: orchestratorClientOptions(),
          extraHeaders: { ...authHeaders },
        });

        if (response.status === 200 && response.body.success) {
          return { ...response.body, success: true };
        }

        return {
          success: false,
          error: 'Failed to fetch webhook MFA configuration',
        };
      } catch (error) {
        console.error('Failed to fetch MFA webhook config:', error);

        return {
          success: false,
          error: 'Failed to fetch webhook MFA configuration',
        };
      }
    },
    [applicationId, tenantId],
  );

  const handleGenerateMailbox = useCallback(async () => {
    if (!applicationId || !tenantId) {
      return { success: false, error: NO_TARGET_ERROR };
    }

    try {
      const authHeaders = await getAuthHeaders();
      const response = await applicationsClient.generateCredentialMailbox({
        params: { id: applicationId },
        body: {},
        overrideClientOptions: orchestratorClientOptions(),
        extraHeaders: { ...authHeaders },
      });

      if (response.status === 200) {
        return response.body;
      }

      return { success: false, error: 'Failed to generate mailbox' };
    } catch (error) {
      console.error('Failed to generate mailbox:', error);

      return { success: false, error: 'Failed to generate mailbox' };
    }
  }, [applicationId, tenantId]);

  return {
    handleFetchMfaMessages,
    handleFetchCredentialInbox,
    handleFetchMfaWebhookConfig,
    handleGenerateMailbox,
  };
}
